// money.ts — SPARK, the single platform currency. ⚡
//
// There is exactly one money unit now: SPARK, and **1 SPARK = 1 cent**. The old
// split of price=`¢` + cash=`$` is gone.
//
// On the wire every monetary value is an int64 *minor unit* serialised as a
// decimal string at MINOR_PRECISION = 4. The wire unit is a "dollar-major"
// value (10000 = $1), so a SPARK figure is that value expressed in cents:
//
//   SPARK = wire / 10^(PRECISION-2) = wire / 100
//
// e.g. wire "5900" (a 0.59 share price) → ⚡59; wire "737500" (a balance) →
// ⚡7,375. Display truncates to 2 dp — never rounds — so a balance never shows a
// sub-cent the user can't spend.
//
// The unit renders as the ⚡ lucide Zap icon via <SparkAmount>. For plain text
// (toasts, aria-labels) use formatSparkText → "59 SPARK".
//
// Wire note: POST bodies still send the backend's `currency: "CREDIT"` literal —
// that's the ledger's internal code, unrelated to the display name. Don't rename it.
//
// i18n (SM-286): DISPLAY formatting (formatSpark) groups digits in the app's
// active language; INPUT parsing (parseSparkToMinor) deliberately stays
// ASCII — typed amounts use "." decimals and tolerate "," grouping in every
// locale, so localized separators must never leak into outbound wire values.
import { getActiveLocale } from "@/lib/i18n";

export const SPARK_MINOR_PRECISION = 4; // int64 minor-unit precision on the wire
export const SPARK_DISPLAY_DP = 2; // decimals shown to users
export const SPARK_LABEL = "GOLD";

// One displayed unit is one dollar, and the wire is a dollar-major value at
// precision 4 — so the divisor IS 10^PRECISION. e.g. wire 737500 → 73.75.
const WIRE_PER_UNIT = 10 ** SPARK_MINOR_PRECISION; // 10 000

/**
 * Wire minor units per ONE displayed unit, for SPARK/GOLD. Other currencies
 * carry their own divisor in the SM-443 presentation contract (`unitMinor`) and
 * pass it through FormatSparkOptions — RED and GREEN are shown on the epic's $1
 * currency unit, where 1 unit = wire 10 000 (SM-484).
 *
 * This is a DISPLAY divisor and nothing else (G2). The wire integer is
 * identical for all three currencies — $1.00 is 10000 in RED, GREEN and GOLD
 * alike — so changing it changes what a number is CALLED, never what it is
 * worth, and no value here is ever rescaled before it is formatted.
 */
export const SPARK_UNIT_MINOR = WIRE_PER_UNIT;

/**
 * Wire digits per displayed unit, i.e. log10(unitMinor).
 *
 * Only exact powers of ten are accepted; anything else falls back to the SPARK
 * divisor. A non-power-of-ten would mean the decimal point does not sit on a
 * digit boundary, and the whole point of the integer path below is that the
 * split is a string slice rather than a division — see the float-drift note.
 * A zero or negative divisor would render Infinity.
 */
function unitShift(unitMinor: number | undefined): number {
  if (unitMinor === undefined) return SPARK_MINOR_PRECISION;
  if (!Number.isFinite(unitMinor) || unitMinor < 1) return SPARK_MINOR_PRECISION;
  const shift = Math.round(Math.log10(unitMinor));
  if (shift < 0 || 10 ** shift !== unitMinor) return SPARK_MINOR_PRECISION;
  return shift;
}

function toMajor(minor: string | number | undefined | null, unitMinor: number): number | null {
  if (minor === undefined || minor === null || minor === "") return null;
  const n = typeof minor === "number" ? minor : Number(minor);
  if (!Number.isFinite(n)) return null;
  return n / unitMinor;
}

export interface FormatSparkOptions {
  /** Decimal places (default 2). Pass the market's price_precision for prices. */
  dp?: number;
  /**
   * Wire minor units per displayed unit (default SPARK_UNIT_MINOR = 10 000).
   * The SM-443 contract passes each currency's `unitMinor`; since SM-460 all
   * three carry this same value, so the option exists to keep the contract
   * explicit rather than to express a difference.
   *
   * It moves the DECIMAL POINT, it does not rescale the amount. The value
   * handed to this function is always the original wire integer, so no digit
   * is lost at any dp — which is precisely what a `n / (unitMinor / 100)`
   * pre-division would do, since that produces a float and the integer path
   * below then truncates it (SM-484).
   */
  unitMinor?: number;
  /** Force a leading "+" on positive values (P&L deltas). */
  sign?: boolean;
  /** Drop a trailing ".00" for whole amounts. */
  trimZeros?: boolean;
  /** Rendered when the input is nullish/NaN (default "—"). */
  placeholder?: string;
}

/**
 * Format int64 minor units as a grouped SPARK number string (no unit symbol),
 * truncated to `dp`. Negative values get a real minus sign (U+2212).
 *   formatSpark("5900")            → "59.00"     (a 0.59 share price → ⚡59)
 *   formatSpark("737500")          → "7,375.00"  (a balance → ⚡7,375)
 *   formatSpark("-5000")           → "−50.00"
 */
export function formatSpark(
  minor: string | number | undefined | null,
  opts: FormatSparkOptions = {},
): string {
  const {
    dp = SPARK_DISPLAY_DP,
    sign = false,
    trimZeros = false,
    placeholder = "—",
    unitMinor,
  } = opts;
  const shift = unitShift(unitMinor);
  const major = toMajor(minor, 10 ** shift);
  if (major === null) return placeholder;
  const digits = truncatedDigits(minor as string | number, dp, trimZeros, shift);
  if (digits === null) return placeholder;
  const body = groupDigits(digits.whole) + (digits.fraction ? decimalSeparator() + digits.fraction : "");
  if (major < 0) return "−" + body;
  if (sign && major > 0) return "+" + body;
  return body;
}

// ── Why the digits are computed in integers, not floats ──────────────
//
// This used to be `Math.trunc(major * 10 ** dp) / 10 ** dp`, and it was
// wrong for roughly one wire value in fifteen. `1999 / 100` is
// 19.990000000000002 in binary floating point; multiplying back by 100
// gives 1998.9999999999998, and truncating that lands on 1998 — so a
// ⚡19.99 balance rendered as ⚡19.98. Same for wire 29 → 0.28, 57 →
// 0.56, 113 → 1.12, 123456789 → 1,234,567.88. Always one unit SHORT,
// never over, which is why it was invisible for so long: it looks like
// conservative rounding rather than a bug.
//
// Truncation is the deliberate rule here (a balance must never display
// more than the user can spend), so it has to be exact. The wire value
// is an integer to begin with, so the whole calculation stays in
// integer space and the only float that survives is the sign test.

interface TruncatedDigits {
  /** Absolute integer part, ungrouped: "1234567". */
  whole: string;
  /** Fraction digits without a separator, already trimmed: "89" or "". */
  fraction: string;
}

/**
 * Split |minor| into display digits at `dp` decimals, truncating toward
 * zero. `minor` is a wire integer at SPARK_MINOR_PRECISION; the SPARK
 * value is minor / WIRE_PER_UNIT, so displaying `dp` decimals keeps
 * the top `dp` digits of that division and drops the rest.
 */
function truncatedDigits(
  minor: string | number,
  dp: number,
  trimZeros: boolean,
  shift: number,
): TruncatedDigits | null {
  const raw = typeof minor === "number" ? String(Math.trunc(minor)) : String(minor).trim();
  const negative = raw.startsWith("-");
  const abs = (negative ? raw.slice(1) : raw).replace(/^\+/, "");
  // Non-integer input (a float that slipped through, scientific
  // notation) — fall back to the float path rather than mangling it.
  if (!/^\d+$/.test(abs)) {
    const n = Math.abs(Number(minor));
    if (!Number.isFinite(n)) return null;
    const f = 10 ** dp;
    const t = Math.trunc((n / 10 ** shift) * f) / f;
    const [w, fr = ""] = t.toFixed(dp).split(".");
    return { whole: w, fraction: trimZeros ? fr.replace(/0+$/, "") : fr };
  }
  // Digits of |minor| shifted so the decimal point sits `shift` digits
  // from the right — 2 for SPARK/GOLD (1 unit = 100 wire), 4 for the $1
  // currency unit RED and GREEN use (1 unit = 10 000 wire). Pad so there
  // is always something on the right of it.
  const padded = abs.padStart(shift + 1, "0");
  const whole = padded.slice(0, padded.length - shift).replace(/^0+(?=\d)/, "");
  const available = padded.slice(padded.length - shift);
  // Keep dp digits, padding with zeros when the wire carries fewer.
  const fractionAll = (available + "0".repeat(Math.max(0, dp - available.length))).slice(0, dp);
  return { whole, fraction: trimZeros ? fractionAll.replace(/0+$/, "") : fractionAll };
}

/** Group an ungrouped integer-digit string in the app language (SM-286). */
function groupDigits(whole: string): string {
  // Values beyond Number.MAX_SAFE_INTEGER cannot round-trip through
  // Number, and a balance is allowed to be one. Group by hand in that
  // case using the locale's own separator.
  const n = Number(whole);
  if (Number.isSafeInteger(n)) {
    try {
      return n.toLocaleString(getActiveLocale(), { useGrouping: true, maximumFractionDigits: 0 });
    } catch {
      return n.toLocaleString(undefined, { useGrouping: true, maximumFractionDigits: 0 });
    }
  }
  const sep = groupSeparator();
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

/** The active locale's decimal separator ("." / "," / "٫"). */
function decimalSeparator(): string {
  return separatorsOf().decimal;
}

/** The active locale's grouping separator. */
function groupSeparator(): string {
  return separatorsOf().group;
}

function separatorsOf(): { decimal: string; group: string } {
  let locale: string | undefined;
  try {
    locale = getActiveLocale();
  } catch {
    locale = undefined;
  }
  try {
    const parts = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      useGrouping: true,
    }).formatToParts(1234.5);
    return {
      decimal: parts.find((p) => p.type === "decimal")?.value ?? ".",
      group: parts.find((p) => p.type === "group")?.value ?? ",",
    };
  } catch {
    return { decimal: ".", group: "," };
  }
}

/**
 * Display decimal places for a price quoted on a market's tick grid. `tickSize`
 * is wire minor units; the tick in display units is tickSize / WIRE_PER_UNIT,
 * so a $0.01 tick → 2 dp and a $0.10 tick → 1 dp. Clamped to
 * [0, SPARK_DISPLAY_DP]; falls back to SPARK_DISPLAY_DP when the tick is
 * unknown. Use as `dp` so book/quote prices render at the grid's precision
 * ($1.00, not a zero-trimmed $1).
 */
export function priceDpForTick(tickSize: string | number | undefined | null): number {
  const minor = typeof tickSize === "number" ? tickSize : Number(tickSize);
  if (!Number.isFinite(minor) || minor <= 0) return SPARK_DISPLAY_DP;
  let step = minor / WIRE_PER_UNIT; // tick in display units
  let dp = 0;
  while (dp < SPARK_DISPLAY_DP && Math.abs(step - Math.round(step)) > 1e-9) {
    step *= 10;
    dp++;
  }
  return dp;
}

/** Plain text with the word unit, for toasts / aria-labels: "12.34 GOLD". */
export function formatSparkText(
  minor: string | number | undefined | null,
  opts: FormatSparkOptions = {},
): string {
  const s = formatSpark(minor, opts);
  const ph = opts.placeholder ?? "—";
  return s === ph ? s : `${s} ${SPARK_LABEL}`;
}

/**
 * Inverse of formatSpark for outbound POSTs: a typed amount → wire minor units.
 * One typed whole unit is one dollar, so "59" → "590000" and "0.50" → "5000".
 *   parseSparkToMinor("59")     → "590000"
 *   parseSparkToMinor("0.50")   → "5000"
 * Commas are tolerated (formatted input). Returns "0" for empty/NaN.
 *
 * **This moved with the divisor.** It used to shift by 10^(precision−2) because
 * a typed unit was one cent; it now shifts by 10^precision because a typed unit
 * is one dollar. Display and entry move together on purpose — a form whose
 * formatter and parser disagree turns "500" into "50,000" on a round trip, and
 * money forms round-trip constantly (open, edit one field, save).
 *
 * The shift is a STRING slice, not a multiplication. `19.99 * 100` is
 * 1998.9999999999998 in binary floating point — the same float drift that made
 * ⚡19.99 render as ⚡19.98 before 06a; the old implementation only escaped it by
 * rounding. Shifting the digits directly lets this truncate — the rule the rest
 * of this module follows — without rounding a typed amount up into money the
 * user did not intend to move.
 */
export function parseSparkToMinor(
  input: string | number | undefined | null,
  precision = SPARK_MINOR_PRECISION,
): string {
  if (input === undefined || input === null || input === "") return "0";
  const raw = String(input).replace(/,/g, "").trim();
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(raw);
  if (!m) {
    // Scientific notation or junk. Fall back to the float path rather than
    // mangling it; a non-finite value reads as zero.
    const n = Number(raw);
    return Number.isFinite(n) ? Math.round(n * 10 ** precision).toString() : "0";
  }
  const [, sgn, whole = "", frac = ""] = m;
  if (whole === "" && frac === "") return "0";
  // Digits of the typed amount with the point moved `precision` places right,
  // padding when fewer were typed and TRUNCATING when more were.
  const shifted = (whole + frac.padEnd(precision, "0").slice(0, precision)).replace(/^0+(?=\d)/, "");
  if (shifted === "" || /^0+$/.test(shifted)) return "0";
  return (sgn === "-" ? "-" : "") + shifted;
}

/** Back-compat alias — prices and amounts use the same minor-unit conversion. */
export const toMinorUnits = parseSparkToMinor;

/**
 * The platform's CANONICAL cash scale: 8dp, one whole unit = 100 000 000
 * (platform/moneyx.Canonical). Ledger and settlement store cash at it; the
 * gateway converts to the 4dp public wire at the /api boundary, so almost
 * nothing a client reads is canonical. The order fee preview (SM-706) is
 * the exception — it answers in this scale directly.
 */
export const CANONICAL_MINOR_PRECISION = 8;

/**
 * A canonical 8dp integer string → the 4dp public wire integer every money
 * renderer here takes: canonicalToWireMinor("100000000") → "10000" (one
 * whole unit), "123456789" → "12345", "-50000" → "-5".
 *
 * TRUNCATES toward zero, the platform's display rule (a figure never reads
 * as more than the ledger holds — moneyx.FormatFromCanonical does the
 * same), and does it as a digit slice so no float ever touches the value.
 * Anything that is not an integer string comes back undefined, which a
 * renderer shows as its "—" placeholder rather than as a wrong number.
 */
export function canonicalToWireMinor(canonical: string | undefined | null): string | undefined {
  if (canonical === undefined || canonical === null) return undefined;
  const m = /^([+-]?)(\d+)$/.exec(String(canonical).trim());
  if (!m) return undefined;
  const [, sgn, digits] = m;
  const shift = CANONICAL_MINOR_PRECISION - SPARK_MINOR_PRECISION;
  const kept = digits.length > shift ? digits.slice(0, digits.length - shift).replace(/^0+(?=\d)/, "") : "0";
  if (/^0+$/.test(kept)) return "0";
  return (sgn === "-" ? "-" : "") + kept;
}

/** Sum two int64 minor-unit strings without precision loss (BigInt). */
export function addMinor(a: string | undefined, b: string | undefined): string {
  const av = a && a !== "" ? BigInt(a) : 0n;
  const bv = b && b !== "" ? BigInt(b) : 0n;
  return (av + bv).toString();
}

// ── SM-463 / SM-517: UNITLESS price display ─────────────────────────
//
// Prices display on the 0.00–1.00 scale per the SM-460 matrix: 1.00 =
// wire 10000 = the resolution unit (one whole RED/GREEN/GOLD). The wire
// integers are untouched (G2) — this is the display layer that replaced
// the retired "0–100 SPARK cents" price lens.
//
// SM-517 took the "$" off the figure. A price is not money: it is the
// per-ticket fraction of the resolution unit, the same number in GAME
// and CASH and under every operator-configured currency. The AMOUNT a
// ticket costs is money, and that is the surface that carries a mark —
// <CurrencyAmount>, which asks the active currency what to draw.

/** One dollar in wire minor units: 10^price_precision at precision 4. */
export const WIRE_PER_DOLLAR = 10 ** SPARK_MINOR_PRECISION; // 10_000

/**
 * Displayed units per one dollar. ONE, since SM-460 put the display on the $1
 * unit — but named rather than dropped, so a call site converting between "the
 * unit a person types" and "dollars" says which conversion it is doing.
 *
 * It was 100 (a typed unit was a cent), spelled as a literal at half a dozen
 * call sites in the trade panel. Those literals did not move when the unit did,
 * which is the whole failure mode: a conversion factor written as a number is
 * invisible to a change that makes it wrong.
 */
export const UNITS_PER_DOLLAR = WIRE_PER_DOLLAR / SPARK_UNIT_MINOR;


/**
 * Display decimals for a price on a market's tick: the fewest decimals
 * that render one tick exactly, floored at 2 (0.10, never 0.1) and
 * capped at the wire precision 4 (SM-443 presentation contract, 03a
 * decision log #4). tick 100 → 2dp, tick 10 → 3dp, tick 1 → 4dp.
 *
 * Distinct from priceDpForTick above, which sizes a PERCENTAGE (the
 * chance lens) — same tick, different scale.
 */
export function priceDisplayDpForTick(tickSize: string | number | undefined | null): number {
  const minor = typeof tickSize === "number" ? tickSize : Number(tickSize);
  if (!Number.isFinite(minor) || minor <= 0) return 2;
  let step = minor / WIRE_PER_DOLLAR;
  let dp = 0;
  while (dp < SPARK_MINOR_PRECISION && Math.abs(step - Math.round(step)) > 1e-9) {
    step *= 10;
    dp++;
  }
  return Math.max(2, dp);
}

/**
 * The executable price band in wire minor units, mirroring the server's
 * `pricing.ExecutableBand` (contracts/pricing/pricing.go): a tradable
 * price sits inside [tick, unit − tick]. Exact 0 and exact `unit` are
 * reserved for resolution, so both edges are one tick in from them
 * (SM-464). `unit` is the market's own price_max — market-service pins
 * it to 10^price_precision so the YES/NO complement (unit − p) stays
 * inside the band — and falls back to WIRE_PER_DOLLAR for a row that
 * carries none. A market with no derivable tick falls back to the
 * finest wire step, which is the same shape as order-service's legacy
 * price_min/price_max path for that market.
 *
 * EVERY wire price the client submits goes through here: a LIMIT resting
 * price, a MARKET worst-price cap, the one-tap sell's floor. A band edge
 * written as a literal is only right at tick 1 — the markets we actually
 * create are tick 100 (price_precision 4), and wire 1 comes back
 * ORDER_000109 "invalid price" before the order reaches a book (SM-548).
 */
export function executableBandWire(
  tickSize?: string | number | null,
  unit?: string | number | null,
): { min: number; max: number; tick: number } {
  const u = Math.floor(Number(unit));
  const t = Math.floor(Number(tickSize));
  const unitWire = Number.isFinite(u) && u > 0 ? u : WIRE_PER_DOLLAR;
  const tick = Number.isFinite(t) && t > 0 ? t : 1;
  // A tick coarser than half the unit leaves no band at all; such a market
  // is unconfigurable for trading, so hand back the widest legal window
  // rather than an inverted one a clamp would silently invert.
  if (tick > unitWire - tick) return { min: 1, max: unitWire - 1, tick };
  return { min: tick, max: unitWire - tick, tick };
}

/**
 * Format a wire price as a plain number on the 0–1 scale: "5500" →
 * "0.55", "10000" → "1.00" (the resolution unit; a display/resolution
 * value, never a resting price). dp follows the market's tick when
 * given. Wire prices are integers so the ÷10^4 is exact.
 *
 * NO unit is attached, by design (SM-517). WIRE_PER_DOLLAR is the
 * divisor because one whole currency unit is what a winning ticket pays
 * — it names the wire scale, not a claim that the result is USD.
 */
export function formatPrice(
  minor: string | number | undefined | null,
  tickSize?: string | number | null,
  opts: { placeholder?: string; dp?: number } = {},
): string {
  const { placeholder = "—" } = opts;
  const n = typeof minor === "number" ? minor : Number(minor);
  if (minor === undefined || minor === null || minor === "" || !Number.isFinite(n)) {
    return placeholder;
  }
  const dp = opts.dp ?? priceDisplayDpForTick(tickSize);
  const price = n / WIRE_PER_DOLLAR;
  const body = Math.abs(price).toLocaleString(getActiveLocale(), {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
  return price < 0 ? `−${body}` : body;
}
